import { Dropdown, IconButton, IDropdownOption, PrimaryButton, Text, TextField } from '@fluentui/react';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';

import {
  DisplayRequiredLabel,
  onRenderLabelEgressOptions,
  onRenderLabelSchema,
  onRenderLabelSelectExistingCdm,
} from '../../../../../Common/Components/Labels';
import { CDMRecord, withDefaultIngestionMethod } from '../../../../../Common/DataOwnerModels/CDMRecord';
import { DataOwnerRequestContext } from '../../../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import { ingestionTypeOptions } from './EditCdmForm';
import * as ToolTips from '../../../../../Common/Components/InformationToolTips';

type AddExistingCdmFormProps = {
  handleCancelAddExistingCdm(): void;
};

export function AddExistingCdmForm(props: AddExistingCdmFormProps): React.ReactElement {
  const { handleCancelAddExistingCdm } = props;

  // Data Owner Request Context
  const {dataOwnerRequestDetails} = useContext(DataOwnerRequestContext);
  const environment: number = dataOwnerRequestDetails.request.environment;
  const nonProdApprovedCdmList: CDMRecord[] = dataOwnerRequestDetails.nonProdApprovedCdms;
  const prodApprovedCdmList: CDMRecord[] = dataOwnerRequestDetails.prodApprovedCdms;

  // Add Existing Cdm State
  const [approvedCdmList, setApprovedCdmList] = useState<CDMRecord[]>([]);
  const [isApprovedCdmListLoading, setIsApprovedCdmListLoading] = useState<boolean>(true);
  const [cdmDropdownOptionList, setCdmDropdownOptionList] = useState<IDropdownOption[]>([]);
  const [selectedExistingCdmId, setSelectedExistingCdmId] = useState<string>('');
  const [selectedExistingCdm, setSelectedExistingCdm] = useState<CDMRecord>(null);
  const [description, setDescription] = useState<string>('');
  const [schema, setSchema] = useState<string>('');
  const [egressionOptions, setEgressionOptions] = useState<number[]>([]);
  const [ingestionType, setIngestionType] = useState<string>('');

  // Add Existing Cdm Errors
  const [selectedExistingCdmError, setSelectedExistingCdmError] = useState<boolean>(false);

  // Button State
  const [isAddExistingCdmButtonDisabled, setIsAddExistingCdmButtonDisabled] = useState<boolean>(true);

  useEffect(() => {
    setIsApprovedCdmListLoading(true);
    let newApprovedCdmList: CDMRecord[] = [];

    // Create list of dropdown options for approved CDM list
    if (environment == 0) {
      newApprovedCdmList = JSON.parse(JSON.stringify(nonProdApprovedCdmList)); // deep copy
    } else {
      newApprovedCdmList = JSON.parse(JSON.stringify(prodApprovedCdmList)); // deep copy
    }
    const newCdmDropdownOptions: IDropdownOption[] = [];
    for (const cdmRecord of newApprovedCdmList) {
      newCdmDropdownOptions.push({
        key: cdmRecord.id,
        text: cdmRecord.name,
      });
    }

    setApprovedCdmList(newApprovedCdmList);
    setCdmDropdownOptionList(newCdmDropdownOptions);
    setIsApprovedCdmListLoading(false);
  }, []);

  useEffect(() => {
    if (selectedExistingCdmId.length === 0) {
      setIsAddExistingCdmButtonDisabled(true);
    } else {
      setIsAddExistingCdmButtonDisabled(false);
    }
  });

  const egressionOptionsDropdownOptions: IDropdownOption[] = [
    { key: 1, text: 'API' },
    { key: 2, text: 'Service Bus' },
    { key: 3, text: 'DataLake' },
    { key: 7, text: 'Snapshot' },
  ];

  const onChangeSelectExistingCdm = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      const cdmId = item.key as string;
      setSelectedExistingCdmId(cdmId);
      setSelectedExistingCdmError(false);
      const cdmRecord: CDMRecord = approvedCdmList.find((cdm) => cdm.id === cdmId);
      setSelectedExistingCdm(cdmRecord);
      setDescription(cdmRecord.description);
      setSchema(cdmRecord.schema);
      setEgressionOptions(cdmRecord.egressOptions);
      setIngestionType(cdmRecord.ingestionType);
    }
  };

  const handleFocusOutSelectExistingCdm = () => {
    if (selectedExistingCdmId.length == 0) {
      setSelectedExistingCdmError(true);
    } else {
      setSelectedExistingCdmError(false);
    }
  };

  const resetAddExistingCdmForm = () => {
    setSelectedExistingCdmId('');
    setSelectedExistingCdm(null);
    setDescription('');
    setSchema('');
    setEgressionOptions([]);
    setSelectedExistingCdmError(false);
  };

  const getCdmIndex = (): number => {
    return dataOwnerRequestDetails.cdms.findIndex((cdmInList: CDMRecord) => {
      return selectedExistingCdm.id === cdmInList.id;
    });
  };

  const handleClickAddExistingCdm = () => {
    const existingCdmDraft: CDMRecord = withDefaultIngestionMethod({
      ...selectedExistingCdm,
      status: 'Draft',
      isExistingCdm: true,
    });
    const cdmIndex = getCdmIndex();
    if (cdmIndex === -1) {
      dataOwnerRequestDetails.addCdmList(existingCdmDraft);
    } else {
      dataOwnerRequestDetails.updateCdmList(cdmIndex, existingCdmDraft);
    }
    resetAddExistingCdmForm();
  };

  const handleClickCancel = () => {
    resetAddExistingCdmForm();
    handleCancelAddExistingCdm();
  };

  return (
    <AddExistingCdmContainer>
      <AddExistingCdmTitleContainer>
        <AddExistingCdmTitleText>Add existing CDM</AddExistingCdmTitleText>
        <IconButton
          data-testid="add-existing-cdm-form-cancel"
          id="AddExistingCdmCancelButton"
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel="Cancel Add Existing CDM Button"
          onClick={() => {
            handleClickCancel();
          }}
          styles={cancelButtonStyles}
        />
      </AddExistingCdmTitleContainer>

      {onRenderLabelSelectExistingCdm(isApprovedCdmListLoading)}
      <Dropdown
        data-testid="add-existing-cdm-form-select"
        id="selectExistingCdm"
        placeholder="Select CDM"
        ariaLabel="Select Existing CDM"
        selectedKey={selectedExistingCdmId}
        onChange={onChangeSelectExistingCdm}
        onBlur={handleFocusOutSelectExistingCdm}
        errorMessage={selectedExistingCdmError ? 'Please select an existing CDM' : ''}
        options={cdmDropdownOptionList}
        disabled={isApprovedCdmListLoading}
      />

      <TextField
        label="Description"
        id="description"
        placeholder="Enter custom description"
        value={description}
        required={false}
        validateOnLoad={false}
        validateOnFocusOut={true}
        multiline={true}
        autoAdjustHeight={true}
        disabled={true}
      />

      <TextField
        onRenderLabel={onRenderLabelSchema}
        id="schema"
        placeholder="Enter Schema in JSON format"
        value={schema}
        multiline={true}
        disabled={true}
      />

      <Dropdown
        onRenderLabel={onRenderLabelEgressOptions}
        id="egressionOptions"
        placeholder="Select Egression"
        ariaLabel="Egression Options"
        selectedKeys={egressionOptions}
        options={egressionOptionsDropdownOptions}
        multiSelect={true}
        disabled={true}
      />

      <Dropdown
        onRenderLabel={() => DisplayRequiredLabel('Ingestion Type', ToolTips.ingestionFileTypeTooltipProps)}
        id="ingestionType"
        placeholder="Select Ingestion Type"
        ariaLabel="IngestionType"
        selectedKey={ingestionType}
        options={ingestionTypeOptions}
        disabled={true}
      />

      <AddExistingCdmButtonContainer>
        <PrimaryButton
          text="Add CDM"
          disabled={isAddExistingCdmButtonDisabled}
          allowDisabledFocus={true}
          onClick={handleClickAddExistingCdm}
        />
      </AddExistingCdmButtonContainer>
    </AddExistingCdmContainer>
  );
}

const AddExistingCdmContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: 20px;
  padding-right: 20px;
  border-radius: 2px;
  background: #fafafa; //grey
  border: 1px solid #000000;
`;

const AddExistingCdmTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
`;

const AddExistingCdmTitleText = styled(Text)`
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ##171717;
`;

// disable flex for button container
const AddExistingCdmButtonContainer = styled.div`
  padding-top: 16px;
`;

const cancelButtonStyles = {
  root: {
    size: '16px',
    height: '16px',
    width: '16px',
    color: '#000',
    margin: '0px 0px 0px 0px',
  },
};
